import { formatUserIdentity } from "@repo/shared";
import { useAuth } from "@workos-inc/authkit-react";
import { useCurrentUser } from "./use-current-user";

export type AuthUserDisplay = {
  /** Empty only until one of the two identity sources has arrived. */
  displayName: string;
  initials: string;
  email: string;
  avatarUrl: string | null;
};

/**
 * How the signed-in person is drawn anywhere the app shows *them* — the sidebar profile, its
 * collapsed avatar, the user menu.
 *
 * There are two records of one person: the AuthKit session, and the Convex `users` row that
 * every feature labels authored content with (comments, clarification threads, assignment
 * rows). They are separate records and can spell the same person differently, so a surface
 * showing one while the rest of the product shows the other is a drift a person can see.
 * **The Convex row wins wherever it exists**; the session answers only before that row has
 * loaded, which is the one moment the row cannot.
 */
export function useAuthUserDisplay(): AuthUserDisplay {
  const { user } = useAuth();
  const currentUser = useCurrentUser();

  const source = currentUser ?? user;
  const { displayName, initials } = formatUserIdentity(source);
  return {
    displayName,
    initials,
    email: source?.email ?? "",
    avatarUrl: currentUser?.profilePictureUrl ?? user?.profilePictureUrl ?? null,
  };
}
